import { Spinner, Text } from "@/components/ui"
import { useAnimatedCurrency } from "@/hooks/use-animated-currency"
import type { BagCoupon } from "@/lib/bag-coupon"
import { centsToReais, formatBRL } from "@/lib/format-brl"
import { TRANSITION } from "@/lib/motion"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import type { ReactNode } from "react"

function CountingAmount({ cents, sign = "none" }: { cents: number; sign?: "none" | "minus" }) {
  const amount = useAnimatedCurrency(centsToReais(cents))
  return (
    <>
      <Text as="strong" weight="semibold" tabular tone="inherit" aria-hidden className="whitespace-nowrap">
        {sign === "minus" ? "−" : null}
        <motion.span>{amount}</motion.span>
      </Text>
      <span className="sr-only">{formatBRL(centsToReais(cents), { sign })}</span>
    </>
  )
}

function Line({ tone = "muted", children }: { tone?: "muted" | "default" | "success" | "warning"; children: ReactNode }) {
  return (
    <Text size="sm" tone={tone} leading="snug">
      {children}
    </Text>
  )
}

function StatusBody({ coupon }: { coupon: BagCoupon }) {
  const { status } = coupon
  switch (status.kind) {
    case "checking":
      return (
        <Text as="span" size="sm" tone="muted" className="flex items-center gap-1.5">
          <Spinner size="xs" />
          Conferindo…
        </Text>
      )
    case "minimum":
      return status.missingCents === null ? (
        <Line>Falta o pedido mínimo</Line>
      ) : (
        <Line tone="default">
          Faltam <CountingAmount cents={status.missingCents} />
        </Line>
      )
    case "applied":
      return (
        <Line tone="default">
          <Text as="span" weight="semibold" tone="success">
            <CountingAmount cents={status.discountCents} sign="minus" />
          </Text>{" "}
          de desconto
        </Line>
      )
    case "at-checkout":
      return status.reason === "delivery" ? (
        <Line tone="success">Liberado: entra na taxa de entrega</Line>
      ) : (
        <Line>Desconto calculado ao finalizar</Line>
      )
    case "blocked":
      return <Line tone="default">{status.hints.join(" · ")}</Line>
    case "not-usable":
      return <Line tone="default">{status.message}</Line>
    case "dropped":
      return <Line>{status.note}</Line>
  }
}

function statusKey(coupon: BagCoupon): string {
  const { status } = coupon
  if (status.kind === "at-checkout") return `${status.kind}-${status.reason}`
  if (status.kind === "minimum") return `${status.kind}-${status.missingCents === null ? "unknown" : "known"}`
  return status.kind
}

/** What one bag coupon is doing now in one short line, cross-faded on every change of state and announced politely. */
export function BagCouponStatusLine({ coupon }: { coupon: BagCoupon }) {
  const reduceMotion = useReducedMotion()
  const offset = reduceMotion ? 0 : 4
  return (
    <div aria-live="polite" aria-atomic="true" className="min-h-5 min-w-0">
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={statusKey(coupon)}
          initial={{ opacity: 0, y: offset }}
          animate={{ opacity: coupon.updating && coupon.status.kind !== "checking" ? 0.7 : 1, y: 0 }}
          exit={{ opacity: 0, y: -offset, transition: TRANSITION.exit }}
          transition={TRANSITION.size}
        >
          <StatusBody coupon={coupon} />
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
